import React, { useCallback } from 'react';
import { Flex, Text, Box, Heading } from 'roo-ui/components';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { themeGet } from 'styled-system';
import TruncateText from 'components/TruncateText';
import ImageGallery from 'components/ImageGallery';
import ImageGalleryThumbnail from 'pages/Property/ImageGalleryThumbnail';
import { useDataLayer } from 'hooks/useDataLayer';
import CheckInInfo from 'pages/Property/CheckInInfo';

const PropertyDescriptionWrapper = styled(Flex)`
  flex-direction: column;
  margin-bottom: ${themeGet('space.10')};
  border-bottom: 1px solid ${themeGet('colors.greys.alto')};
  padding-bottom: ${themeGet('space.10')};
`;

const PropertyImageWrapper = styled(Flex)`
  justify-content: center;
`;

const PropertyDescriptionTextWrapper = styled(Box)`
  margin: 0 auto;
  margin-top: ${themeGet('space.4')};
`;

const PropertyDescription = ({ property }) => {
  const { emitInteractionEvent } = useDataLayer();
  const theme = useTheme();
  const imageCount = property.images.length;
  const { height, width } = theme.imageSizes.propertyDescription;

  const handleImageClick = useCallback(() => {
    emitInteractionEvent({ type: 'Property Description Gallery', value: 'Gallery Opened' });
  }, [emitInteractionEvent]);

  const handleOnImageChanged = useCallback(
    (id) => {
      emitInteractionEvent({ type: 'Property Description Gallery', value: `Gallery Scrolled to ${id}` });
    },
    [emitInteractionEvent],
  );

  const handleOnClick = useCallback(() => {
    emitInteractionEvent({ type: 'Property Detail Pop Up', value: 'Read More Selected' });
  }, [emitInteractionEvent]);

  return (
    <PropertyDescriptionWrapper>
      <Box>
        <Flex flex="1 1 auto" flexDirection="column" textAlign="center" mb={[4, 1]}>
          <Text fontSize="base" color="greys.steel" data-testid="property-name">
            {property.name}
          </Text>
          <Heading.h2 color="greys.charcoal" mb={3} fontSize="xl">
            About this property
          </Heading.h2>
        </Flex>
        <PropertyImageWrapper my={[5, 10]}>
          <ImageGallery
            images={property.images}
            width={width}
            onClick={handleImageClick}
            onImageChanged={handleOnImageChanged}
            imageSize="large"
            hideThumbnails
          >
            <ImageGalleryThumbnail
              height={height}
              width={width}
              src={property?.mainImage?.urlLarge}
              alt={property?.mainImage?.caption}
              imageCount={imageCount}
              counterSize="large"
              lazy
            />
          </ImageGallery>
        </PropertyImageWrapper>
        <PropertyDescriptionTextWrapper>
          <Text fontSize="base" onClick={handleOnClick} data-testid="truncate-text-GA-wrapper">
            <TruncateText text={property.description} maxHeight="12em" modalTitle={`About this property`} />
          </Text>
          <CheckInInfo property={property} mb={3} mt={[7, 10, 6]} />
        </PropertyDescriptionTextWrapper>
      </Box>
    </PropertyDescriptionWrapper>
  );
};

PropertyDescription.propTypes = {
  property: PropTypes.object.isRequired,
};

export default PropertyDescription;
